import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Share2, Link as LinkIcon, Check, Download, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PUBLIC_URL } from "@/lib/shareUrl";

/**
 * Attempt to attach the site poster (public/share-poster.png) as a File
 * to the given share payload, so WhatsApp / Messages / Email receive
 * both text+link AND the poster image. Returns a new payload with
 * `files` when the browser supports it, otherwise `null` so the caller
 * can fall back to text-only sharing.
 *
 * We fetch the poster lazily (first share only) — cache is handled by
 * the browser after that. If ANY step fails we return null and the
 * caller keeps the classic text/url share.
 */
async function tryAttachPoster(baseShare) {
  try {
    if (typeof navigator === "undefined" || !navigator.canShare) return null;
    const res = await fetch("/share-poster.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    const file = new File([blob], "my-world-quest-poster.png", { type: blob.type || "image/png" });
    const payload = { ...baseShare, files: [file] };
    if (!navigator.canShare(payload)) return null;
    return payload;
  } catch {
    return null;
  }
}

/**
 * <ShareButton /> — universal kid-safe share affordance for pages, games and
 * activities. Renders a compact circular button. When tapped, opens a modal
 * with three actions:
 *   1. Copy link (to clipboard)
 *   2. Native share (when navigator.share is available — mobile/tablet)
 *   3. QR code (rendered as SVG; downloadable as PNG) — perfect for sharing
 *      from a parent's screen to a kid's tablet.
 *
 * Props:
 *   - path:  optional override route to share (e.g. "/squishy"). When omitted,
 *            shares the current location.
 *   - title: optional display title used by Web Share API + dialog heading.
 *   - label: optional descriptive subtitle (e.g. "Squishy Hop · Math game").
 *   - size:  "sm" (default) | "md" — controls button diameter.
 *   - variant: "ghost" (default, transparent) | "solid" (yellow chip)
 *   - triggerLabel: optional string. When set, the trigger renders as a
 *                   labeled pill (text + icon) instead of a circular icon
 *                   button — ideal for celebratory CTAs (certificates,
 *                   level-up modals).
 *   - testId: custom data-testid (defaults to "share-btn").
 *   - className: extra classes for the trigger button.
 */
export default function ShareButton({
  path,
  title,
  label,
  size = "sm",
  variant = "ghost",
  triggerLabel,
  testId = "share-btn",
  className,
}) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build the full absolute URL the QR / share will point to.
  // Always uses the canonical PUBLIC_URL (myworld.quest) so links from the
  // preview/staging environments still take recipients to production.
  const url = useMemo(() => {
    const target = path || location.pathname + location.search;
    const clean = target.startsWith("/") ? target : `/${target}`;
    return `${PUBLIC_URL}${clean}`;
  }, [path, location.pathname, location.search]);

  const shareTitle = title || "My World Quest";
  const shareText = label
    ? `${shareTitle} — ${label} 🌍`
    : `Explore ${shareTitle} on My World Quest 🌍`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied!");
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the link to copy it.");
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopy();
      return;
    }
    // Try to attach the poster as a file (Web Share API Level 2). Works
    // for WhatsApp/Messages/Email on modern mobile browsers. If files
    // aren't supported OR the fetch fails, degrade to text+URL only —
    // recipient still gets the link.
    try {
      const shareData = { title: shareTitle, text: shareText, url };
      const withPoster = await tryAttachPoster(shareData);
      await navigator.share(withPoster || shareData);
    } catch {
      /* user dismissed — no toast needed */
    }
  };

  const handleDownloadPoster = () => {
    // Direct download for parents who want the poster to attach manually
    // (some desktop mail clients don't support Web Share API).
    const a = document.createElement("a");
    a.href = "/share-poster.png";
    a.download = "my-world-quest-poster.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Poster saved!");
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById("share-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const SIZE = 512;
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${(title || "my-world-quest").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-qr.png`;
      a.click();
      toast.success("QR saved!");
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  // G2 · Minimum 44×44 touch target on all icon-only buttons.
  // `size="md"` = comfy 48px (Seedlings-safe); default = the 44px floor.
  // We also lock the min-width/min-height so a shrinking flex parent
  // can't squeeze the button below the touch-target guardrail (the
  // 41×41 audit failure was traced to this).
  const sizeClasses =
    size === "md"
      ? "h-12 w-12 min-h-12 min-w-12 md:h-12 md:w-12 shrink-0"
      : "h-11 w-11 min-h-11 min-w-11 md:h-11 md:w-11 shrink-0";

  const variantClasses =
    variant === "solid"
      ? "bg-yellow-300 border-slate-900"
      : "bg-white border-slate-900";

  const dialogJsx = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-slate-900/55 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="share-dialog-overlay"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Share ${shareTitle}`}
            className="relative w-full max-w-sm rounded-3xl border-4 border-slate-900 bg-white shadow-[10px_10px_0_0_rgba(15,23,42,1)] p-5 md:p-6"
            data-testid="share-dialog"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="share-dialog-close"
              aria-label="Close"
              className="absolute top-3 right-3 h-11 w-11 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all"
            >
              <X size={18} strokeWidth={3} />
            </button>

            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border-[3px] border-slate-900 bg-yellow-300 shadow-[0_3px_0_0_rgba(15,23,42,1)]">
                <Share2 size={18} strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <h3 className="font-heading text-lg md:text-xl font-bold leading-tight truncate">
                  Share {shareTitle}
                </h3>
                {label ? (
                  <p className="text-xs font-bold text-slate-600 truncate">{label}</p>
                ) : null}
              </div>
            </div>

            {/* QR code */}
            <div
              className="rounded-2xl border-[3px] border-slate-900 bg-white p-4 flex items-center justify-center"
              data-testid="share-qr-wrap"
            >
              <QRCodeSVG
                id="share-qr-svg"
                value={url}
                size={184}
                level="M"
                marginSize={2}
                bgColor="#FFFFFF"
                fgColor="#0F172A"
              />
            </div>
            <p className="mt-2 text-center text-[11px] font-bold uppercase tracking-widest text-slate-500">
              Scan to open on another device
            </p>

            {/* URL row */}
            <div className="mt-3 flex items-center gap-2 rounded-xl border-[3px] border-slate-900 bg-slate-50 px-3 py-2">
              <LinkIcon size={16} strokeWidth={3} className="text-slate-500 shrink-0" />
              <input
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                data-testid="share-url-input"
                className="flex-1 min-w-0 bg-transparent font-bold text-xs md:text-sm text-slate-800 outline-none"
              />
            </div>

            {/* Actions */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="share-copy-link"
                className={cn(
                  "inline-flex items-center justify-center gap-2 h-11 rounded-2xl border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all",
                  copied ? "bg-emerald-300" : "bg-yellow-300",
                )}
              >
                {copied ? <Check size={16} strokeWidth={3} /> : <LinkIcon size={16} strokeWidth={3} />}
                {copied ? "Copied!" : "Copy link"}
              </button>
              <button
                type="button"
                onClick={handleDownloadQR}
                data-testid="share-download-qr"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-2xl border-[3px] border-slate-900 bg-sky-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all"
              >
                <Download size={16} strokeWidth={3} />
                Save QR
              </button>
            </div>

            {/* Poster preview — shown as the artwork that WhatsApp/Email
                recipients will receive attached to the share link. */}
            <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-2" data-testid="share-poster-preview">
              <div className="flex items-center gap-2">
                <img
                  src="/share-poster.png"
                  alt="My World Quest promotional poster"
                  className="h-16 w-12 object-cover rounded-lg border-2 border-slate-900 shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 flex items-center gap-1">
                    <ImageIcon size={11} strokeWidth={3} /> Included with share
                  </p>
                  <p className="text-xs font-bold text-slate-700 leading-snug">
                    The kid-friendly poster attaches automatically when you share via WhatsApp, Messages or Email.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadPoster}
                  data-testid="share-download-poster"
                  title="Save poster to attach manually"
                  className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                >
                  <Download size={14} strokeWidth={3} />
                </button>
              </div>
            </div>

            {typeof navigator !== "undefined" && navigator.share ? (
              <button
                type="button"
                onClick={handleNativeShare}
                data-testid="share-native"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 h-11 rounded-2xl border-[3px] border-slate-900 bg-emerald-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all"
              >
                <Share2 size={16} strokeWidth={3} />
                Share via apps…
              </button>
            ) : null}

            <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Kid-safe — no account needed
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Pill (labeled) trigger — used in celebratory moments like certificates.
  if (triggerLabel) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
          data-testid={testId}
          title={`Share ${shareTitle}`}
          aria-label={`Share ${shareTitle}`}
          className={cn(
            "inline-flex items-center justify-center gap-2 h-11 px-4 rounded-2xl border-[3px] border-slate-900 font-bold text-sm md:text-base shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all",
            variant === "solid" ? "bg-yellow-300" : "bg-emerald-300",
            className,
          )}
        >
          <Share2 size={16} strokeWidth={3} />
          <span className="whitespace-nowrap">{triggerLabel}</span>
        </button>
        {dialogJsx}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Stop propagation so clicking the share icon inside a <Link> tile
          // doesn't also navigate to the underlying route.
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid={testId}
        title="Share this page"
        aria-label={`Share ${shareTitle}`}
        className={cn(
          "inline-flex items-center justify-center rounded-full border-[3px] shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all",
          sizeClasses,
          variantClasses,
          className,
        )}
      >
        <Share2 size={size === "md" ? 18 : 14} strokeWidth={3} />
      </button>
      {dialogJsx}
    </>
  );
}
